// Verwaltung: Konten freischalten und sperren, Einladungslinks ausstellen.
//
// Nur fuer Admins. Die API antwortet allen anderen mit 404, diese Seite ist
// also die Bequemlichkeit und nicht die Absicherung.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fehlertext } from "../lib/api.js";
import { Hinweis, Karte, Knopf, Rahmen } from "../bausteine/Bausteine.js";

interface AdminNutzer {
  id: string;
  email: string;
  displayName: string | null;
  status: "pending" | "active" | "disabled";
  role: "admin" | "user";
  createdAt: string;
  lastLoginAt: string | null;
}

interface AdminEinladung {
  id: number;
  label: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
}

function datum(wert: string | null): string {
  if (!wert) return "nie";
  return new Date(wert).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function Verwaltung() {
  const queryClient = useQueryClient();
  const [label, setzeLabel] = useState("");
  const [neuerLink, setzeNeuenLink] = useState<string | null>(null);

  const nutzer = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api<{ nutzer: AdminNutzer[] }>("/admin/users"),
  });

  const einladungen = useQuery({
    queryKey: ["admin", "invites"],
    queryFn: () => api<{ einladungen: AdminEinladung[] }>("/admin/invites"),
  });

  const entscheiden = useMutation({
    mutationFn: ({ id, was }: { id: string; was: "approve" | "reject" | "disable" | "enable" }) =>
      api(`/admin/users/${id}/${was}`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const einladen = useMutation({
    mutationFn: () => api<{ url: string }>("/admin/invites", { method: "POST", body: { label, tage: 14 } }),
    onSuccess: (antwort) => {
      // Der Klartext des Tokens kommt genau einmal. Wer ihn wegklickt, stellt
      // einen neuen aus; nachschlagen laesst er sich nicht.
      setzeNeuenLink(antwort.url);
      setzeLabel("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });

  const loeschen = useMutation({
    mutationFn: (id: number) => api<void>(`/admin/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "invites"] }),
  });

  const offen = nutzer.data?.nutzer.filter((n) => n.status === "pending") ?? [];
  const uebrige = nutzer.data?.nutzer.filter((n) => n.status !== "pending") ?? [];

  return (
    <Rahmen
      titel="Verwaltung"
      kinder={
        <>
          <Karte
            kinder={
              <>
                <h2 className="mb-2 text-base font-medium">
                  Offene Anfragen {offen.length > 0 && <span className="text-akzent">({offen.length})</span>}
                </h2>
                {nutzer.error && <Hinweis art="fehler" kinder={fehlertext(nutzer.error)} />}
                {offen.length === 0 && <p className="text-sm text-neutral-600">Nichts zu tun.</p>}
                <ul className="divide-y divide-neutral-200">
                  {offen.map((n) => (
                    <li key={n.id} className="py-3">
                      <p className="text-sm font-medium">{n.displayName ?? "ohne Namen"}</p>
                      <p className="mb-2 text-sm text-neutral-600">{n.email}</p>
                      <p className="mb-2 text-xs text-neutral-500">angefragt am {datum(n.createdAt)}</p>
                      <div className="flex gap-2">
                        <Knopf
                          onClick={() => entscheiden.mutate({ id: n.id, was: "approve" })}
                          disabled={entscheiden.isPending}
                          kinder="Freischalten"
                        />
                        <Knopf
                          art="warnung"
                          onClick={() => entscheiden.mutate({ id: n.id, was: "reject" })}
                          disabled={entscheiden.isPending}
                          kinder="Ablehnen"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            }
          />

          <Karte
            kinder={
              <>
                <h2 className="mb-2 text-base font-medium">Konten</h2>
                <ul className="divide-y divide-neutral-200">
                  {uebrige.map((n) => (
                    <li key={n.id} className="flex items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm">{n.displayName ?? n.email}</p>
                        <p className="text-xs text-neutral-500">
                          {n.status === "active" ? "aktiv" : "gesperrt"}
                          {n.role === "admin" && " \u00b7 Admin"} \u00b7 zuletzt {datum(n.lastLoginAt)}
                        </p>
                      </div>
                      {n.status === "active" ? (
                        <Knopf
                          art="warnung"
                          onClick={() => entscheiden.mutate({ id: n.id, was: "disable" })}
                          kinder="Sperren"
                        />
                      ) : (
                        <Knopf
                          art="neben"
                          onClick={() => entscheiden.mutate({ id: n.id, was: "enable" })}
                          kinder="Entsperren"
                        />
                      )}
                    </li>
                  ))}
                </ul>
                {entscheiden.error && <Hinweis art="fehler" kinder={fehlertext(entscheiden.error)} />}
              </>
            }
          />

          <Karte
            kinder={
              <>
                <h2 className="mb-1 text-base font-medium">Einladungslinks</h2>
                <p className="mb-3 text-sm text-neutral-600">
                  Wer damit hereinkommt, ist sofort freigeschaltet. Jeder Link gilt einmal und
                  läuft nach 14 Tagen ab.
                </p>

                {neuerLink && (
                  <Hinweis
                    art="gut"
                    kinder={
                      <>
                        <span className="block font-medium">Einmal sichtbar, jetzt kopieren:</span>
                        <code className="mt-1 block break-all text-xs">{neuerLink}</code>
                      </>
                    }
                  />
                )}
                {einladen.error && <Hinweis art="fehler" kinder={fehlertext(einladen.error)} />}

                <form
                  className="mb-3 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    einladen.mutate();
                  }}
                >
                  <input
                    value={label}
                    onChange={(e) => setzeLabel(e.target.value)}
                    placeholder="Für wen?"
                    maxLength={60}
                    className="min-h-11 flex-1 rounded border border-neutral-300 px-3 text-sm outline-none focus:border-akzent"
                  />
                  <Knopf type="submit" disabled={label.trim().length < 2 || einladen.isPending} kinder="Ausstellen" />
                </form>

                <ul className="divide-y divide-neutral-200 text-sm">
                  {einladungen.data?.einladungen.map((e) => (
                    <li key={e.id} className="flex items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <p className="truncate">{e.label}</p>
                        <p className="text-xs text-neutral-500">
                          {e.usedAt ? `eingelöst am ${datum(e.usedAt)}` : `gültig bis ${datum(e.expiresAt)}`}
                        </p>
                      </div>
                      <Knopf art="neben" onClick={() => loeschen.mutate(e.id)} kinder="Löschen" />
                    </li>
                  ))}
                </ul>
              </>
            }
          />

          <Link to="/" className="text-sm text-akzent underline">
            Zurück
          </Link>
        </>
      }
    />
  );
}
